/**
 * Tassonomia sorgente appuntamento (mirror del modulo backend
 * `packages/backend/src/utils/appointment-source-taxonomy.ts`).
 *
 * Mantenere SINCRONIZZATI: backend, frontend, e funzione SQL
 * `compute_appointment_source_kind` nella migration
 * `add_source_kind_to_appointments.sql`.
 *
 * NOTA: l'endpoint `GET /calendar/source-taxonomy` espone la stessa lista
 * lato server per ispezione runtime (es. tooling/admin), ma il frontend
 * usa questa copia statica come unica fonte per rendering e tipi
 * TypeScript — eventuali nuove sorgenti vanno aggiunte qui e nella
 * migration SQL, non solo lato backend.
 */

export type AppointmentSourceKind =
  | 'form_pubblico'
  | 'lead_campagna'
  | 'drive_to_store'
  | 'ai_chatbot_web'
  | 'ai_voicebot_inbound'
  | 'callback_operatore'
  | 'manuale_admin'
  | 'sistema_evergreen'
  | 'api_esterna';

export interface AppointmentSourceDescriptor {
  kind: AppointmentSourceKind;
  label: string;
  description: string;
  icon: string;
  bgClass: string;
  textClass: string;
  borderClass: string;
}

export const APPOINTMENT_SOURCE_TAXONOMY: AppointmentSourceDescriptor[] = [
  {
    kind: 'form_pubblico',
    label: 'Form pubblico',
    description: 'Prenotazione tramite un form pubblico (pagina /book/...).',
    icon: 'FileText',
    bgClass: 'bg-sky-100 dark:bg-sky-900/30',
    textClass: 'text-sky-700 dark:text-sky-300',
    borderClass: 'border-sky-300 dark:border-sky-700',
  },
  {
    kind: 'lead_campagna',
    label: 'Lead campagna',
    description: 'Generato da un lead CRM legato a una campagna marketing.',
    icon: 'Target',
    bgClass: 'bg-violet-100 dark:bg-violet-900/30',
    textClass: 'text-violet-700 dark:text-violet-300',
    borderClass: 'border-violet-300 dark:border-violet-700',
  },
  {
    kind: 'drive_to_store',
    label: 'Drive-to-store',
    description: 'Visita programmata in punto vendita da campagna drive-to-store.',
    icon: 'Store',
    bgClass: 'bg-amber-100 dark:bg-amber-900/30',
    textClass: 'text-amber-800 dark:text-amber-300',
    borderClass: 'border-amber-300 dark:border-amber-700',
  },
  {
    kind: 'ai_chatbot_web',
    label: 'AI chatbot web',
    description: 'Prenotazione raccolta dal chatbot AI sul sito.',
    icon: 'Bot',
    bgClass: 'bg-emerald-100 dark:bg-emerald-900/30',
    textClass: 'text-emerald-700 dark:text-emerald-300',
    borderClass: 'border-emerald-300 dark:border-emerald-700',
  },
  {
    kind: 'ai_voicebot_inbound',
    label: 'AI voicebot inbound',
    description: 'Appuntamento creato dal voicebot AI durante una chiamata in entrata.',
    icon: 'PhoneIncoming',
    bgClass: 'bg-teal-100 dark:bg-teal-900/30',
    textClass: 'text-teal-700 dark:text-teal-300',
    borderClass: 'border-teal-300 dark:border-teal-700',
  },
  {
    kind: 'callback_operatore',
    label: 'Callback operatore',
    description: 'Richiamo programmato da un operatore (telefono / dialer).',
    icon: 'PhoneCall',
    bgClass: 'bg-blue-100 dark:bg-blue-900/30',
    textClass: 'text-blue-700 dark:text-blue-300',
    borderClass: 'border-blue-300 dark:border-blue-700',
  },
  {
    kind: 'manuale_admin',
    label: 'Manuale (admin)',
    description: 'Inserito manualmente da un operatore dalla UI calendario/CRM.',
    icon: 'UserCog',
    bgClass: 'bg-slate-100 dark:bg-slate-800/50',
    textClass: 'text-slate-700 dark:text-slate-300',
    borderClass: 'border-slate-300 dark:border-slate-600',
  },
  {
    kind: 'sistema_evergreen',
    label: 'Sistema / Evergreen',
    description: 'Generato automaticamente dal sistema (campagne evergreen, video room, ecc.).',
    icon: 'Sparkles',
    bgClass: 'bg-indigo-100 dark:bg-indigo-900/30',
    textClass: 'text-indigo-700 dark:text-indigo-300',
    borderClass: 'border-indigo-300 dark:border-indigo-700',
  },
  {
    kind: 'api_esterna',
    label: 'API esterna',
    description: 'Creato via API pubblica, MCP server o workflow engine.',
    icon: 'Plug',
    bgClass: 'bg-fuchsia-100 dark:bg-fuchsia-900/30',
    textClass: 'text-fuchsia-700 dark:text-fuchsia-300',
    borderClass: 'border-fuchsia-300 dark:border-fuchsia-700',
  },
];

export const APPOINTMENT_SOURCE_KINDS: AppointmentSourceKind[] =
  APPOINTMENT_SOURCE_TAXONOMY.map(t => t.kind);

const TAXONOMY_BY_KIND: Record<string, AppointmentSourceDescriptor> =
  APPOINTMENT_SOURCE_TAXONOMY.reduce((acc, t) => {
    acc[t.kind] = t;
    return acc;
  }, {} as Record<string, AppointmentSourceDescriptor>);

export function getSourceDescriptor(
  kind: string | null | undefined
): AppointmentSourceDescriptor | null {
  if (!kind) return null;
  return TAXONOMY_BY_KIND[kind] || null;
}
